/**
 * The two-decimal percentage convention: user-facing percentages carry up to two decimals
 * (`67.25%`) and are stored as fractions (`0.6725`). The display side is the `fsfPercent` pipe
 * in `@fsf/ui`. See AGENTS.md.
 */

/** Round a percentage to two decimal places (`67.256` → `67.26`, `67.254` → `67.25`). */
export function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Rounds to four decimals so the fraction is exactly the percentage value (`99.99` → `0.9999`,
 * not `0.9998999999999999`), which keeps the API's `decimal:0,4` acceptance self-contained
 * instead of leaning on backend coercion.
 */
export function percentToFraction(pct: number): number {
  return Math.round(pct * 100) / 10000;
}

/** Convert a `[0,1]` fraction to a two-decimal percentage (`0.6725` → `67.25`). */
export function fractionToPercent(fraction: number): number {
  return roundToTwoDecimals(fraction * 100);
}

/**
 * Clamp into `[0,100]` and round to two decimals (`120` → `100`, `-5` → `0`, `33.333` →
 * `33.33`). The one entry point for sanitizing a typed percentage before it is stored.
 */
export function clampPercent(value: number): number {
  return roundToTwoDecimals(Math.min(100, Math.max(0, value)));
}
